/**
 * Server-administration server state (CLAUDE.md 12.2).
 *
 * Separate from `api/hooks.ts` on purpose. Everything in there is workspace-scoped and
 * keyed by the active workspace; nothing here is — server administration is explicitly
 * *not* a business role (11.2), and a sysadmin listing every workspace must not have
 * their results filed under whichever workspace they happen to be standing in. Keeping
 * the two key spaces apart is what stops a workspace switch invalidating the admin
 * cache, and stops an admin query leaking into an engineer's.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import type {
  AdminDashboard,
  AdminModule,
  AdminQueue,
  AdminSession,
  AdminSetting,
  AdminSheetTemplate,
  AdminTicket,
  AdminTicketDetail,
  AdminTicketSummary,
  AdminUser,
  AdminWorkspace,
  AuditEntry,
  Page,
  PasswordResetLink,
} from '@/api/types'
import type { TicketMessage } from '@/api/support'

export const PAGE_SIZE = 25

export const adminKeys = {
  all: ['admin'] as const,
  dashboard: ['admin', 'dashboard'] as const,
  queue: ['admin', 'queue'] as const,
  users: (query: string) => ['admin', 'users', query] as const,
  workspaces: (query: string) => ['admin', 'workspaces', query] as const,
  sessions: (query: string) => ['admin', 'sessions', query] as const,
  settings: ['admin', 'settings'] as const,
  modules: ['admin', 'modules'] as const,
  sheetTemplates: ['admin', 'sheet-templates'] as const,
  tickets: (query: string) => ['admin', 'tickets', query] as const,
  ticketSummary: ['admin', 'tickets', 'summary'] as const,
  ticket: (id: number) => ['admin', 'ticket', id] as const,
  audit: (query: string) => ['admin', 'audit', query] as const,
}

/** Drops every admin query, so a mutation cannot leave a stale count on the overview. */
function useInvalidateAdmin() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: adminKeys.all })
  }
}

function search(params: Record<string, string | number | boolean | undefined>): string {
  const usable = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== '' && value !== null,
  )
  return new URLSearchParams(usable.map(([k, v]) => [k, String(v)])).toString()
}

/* --- overview -------------------------------------------------------------------- */

export function useAdminDashboard() {
  return useQuery({
    queryKey: adminKeys.dashboard,
    queryFn: () => api.get<AdminDashboard>('/admin/dashboard'),
    // Counts, not a live monitor. Refetching every few seconds would put a scan of
    // every table behind an idle browser tab.
    staleTime: 30_000,
  })
}

export function useAdminQueue() {
  return useQuery({
    queryKey: adminKeys.queue,
    queryFn: () => api.get<AdminQueue>('/admin/queue'),
    staleTime: 15_000,
  })
}

/* --- users ----------------------------------------------------------------------- */

export function useAdminUsers(filters: {
  q?: string
  is_active?: boolean
  system_role?: string
  offset?: number
}) {
  const query = search({ ...filters, limit: PAGE_SIZE })
  return useQuery({
    queryKey: adminKeys.users(query),
    queryFn: () => api.get<Page<AdminUser>>(`/admin/users?${query}`),
    placeholderData: (previous) => previous,
  })
}

export function useUpdateAdminUser() {
  const invalidate = useInvalidateAdmin()
  return useMutation({
    mutationFn: ({
      userId,
      ...body
    }: {
      userId: number
      is_active?: boolean
      system_role?: 'user' | 'sysadmin'
      display_name?: string
      unlock?: boolean
      require_password_change?: boolean
    }) => api.patch<AdminUser>(`/admin/users/${userId}`, body),
    onSuccess: invalidate,
  })
}

/**
 * Mint a reset link for an account whose owner cannot receive mail.
 *
 * The response is the only place the token exists outside the recipient's browser, so
 * the caller must show it immediately — exactly like a share link (11.4). It is
 * deliberately not cached.
 */
export function useMintPasswordResetLink() {
  return useMutation({
    mutationFn: (userId: number) =>
      api.post<PasswordResetLink>(`/admin/users/${userId}/password-reset-link`),
  })
}

export function useRevokeUserSessions() {
  const invalidate = useInvalidateAdmin()
  return useMutation({
    mutationFn: (userId: number) =>
      api.post<{ revoked: number }>(`/admin/users/${userId}/revoke-sessions`),
    onSuccess: invalidate,
  })
}

/* --- workspaces ------------------------------------------------------------------ */

export function useAdminWorkspaces(filters: { q?: string; kind?: string; offset?: number }) {
  const query = search({ ...filters, limit: PAGE_SIZE })
  return useQuery({
    queryKey: adminKeys.workspaces(query),
    queryFn: () => api.get<Page<AdminWorkspace>>(`/admin/workspaces?${query}`),
    placeholderData: (previous) => previous,
  })
}

export function useUpdateAdminWorkspace() {
  const invalidate = useInvalidateAdmin()
  return useMutation({
    mutationFn: ({
      workspaceId,
      ...body
    }: {
      workspaceId: number
      is_active?: boolean
      disk_quota_bytes?: number
    }) => api.patch<AdminWorkspace>(`/admin/workspaces/${workspaceId}`, body),
    onSuccess: invalidate,
  })
}

/* --- sessions -------------------------------------------------------------------- */

export function useAdminSessions(filters: {
  user_id?: number
  active_only?: boolean
  offset?: number
}) {
  const query = search({ ...filters, limit: PAGE_SIZE })
  return useQuery({
    queryKey: adminKeys.sessions(query),
    queryFn: () => api.get<Page<AdminSession>>(`/admin/sessions?${query}`),
    placeholderData: (previous) => previous,
  })
}

export function useRevokeSession() {
  const invalidate = useInvalidateAdmin()
  return useMutation({
    mutationFn: (sessionId: number) => api.delete<void>(`/admin/sessions/${sessionId}`),
    onSuccess: invalidate,
  })
}

/* --- global settings ------------------------------------------------------------- */

export function useAdminSettings() {
  return useQuery({
    queryKey: adminKeys.settings,
    queryFn: () => api.get<{ settings: AdminSetting[] }>('/admin/settings'),
  })
}

export function useUpdateAdminSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      api.patch<{ settings: AdminSetting[] }>('/admin/settings', { values }),
    onSuccess: (data) => {
      queryClient.setQueryData(adminKeys.settings, data)
    },
  })
}

/* --- modules --------------------------------------------------------------------- */

export function useAdminModules() {
  return useQuery({
    queryKey: adminKeys.modules,
    queryFn: () => api.get<AdminModule[]>('/admin/modules'),
  })
}

export function useUpdateAdminModule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      moduleId,
      ...body
    }: {
      moduleId: string
      enabled?: boolean
      disabled_codes?: string[]
    }) => api.patch<AdminModule>(`/admin/modules/${moduleId}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.modules })
      // The engineer-facing registry is narrowed by the same overlay, so it is now
      // stale too — otherwise a module disabled here keeps appearing in the picker
      // until the session ends.
      void queryClient.invalidateQueries({ queryKey: ['modules'] })
    },
  })
}

/* --- sheet starters (ADR-0013) ----------------------------------------------------- */

export function useAdminSheetTemplates() {
  return useQuery({
    queryKey: adminKeys.sheetTemplates,
    queryFn: () => api.get<AdminSheetTemplate[]>('/admin/sheet-templates'),
  })
}

export function useUpdateAdminSheetTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch<AdminSheetTemplate>(`/admin/sheet-templates/${id}`, { enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.sheetTemplates })
      // The gallery engineers see is narrowed by the same overlay, so it is stale too.
      void queryClient.invalidateQueries({ queryKey: ['sheet-templates'] })
    },
  })
}

/* --- tickets --------------------------------------------------------------------- */

export interface AdminTicketFilters {
  status?: string
  type?: string
  priority?: string
  assignee_id?: number
  awaiting?: boolean
  q?: string
  offset?: number
}

export function useAdminTickets(filters: AdminTicketFilters) {
  const query = search({ ...filters, limit: PAGE_SIZE })
  return useQuery({
    queryKey: adminKeys.tickets(query),
    queryFn: () => api.get<Page<AdminTicket>>(`/admin/tickets?${query}`),
    placeholderData: (previous) => previous,
  })
}

/** Counts for the queue tabs, so a filter shows its size before it is clicked. */
export function useAdminTicketSummary() {
  return useQuery({
    queryKey: adminKeys.ticketSummary,
    queryFn: () => api.get<AdminTicketSummary>('/admin/tickets/summary'),
  })
}

export function useAdminTicket(ticketId: number | null) {
  return useQuery({
    queryKey: adminKeys.ticket(ticketId ?? 0),
    queryFn: () => api.get<AdminTicketDetail>(`/admin/tickets/${ticketId}`),
    enabled: ticketId !== null && Number.isFinite(ticketId),
  })
}

export function useUpdateAdminTicket() {
  const invalidate = useInvalidateAdmin()
  return useMutation({
    mutationFn: ({
      ticketId,
      ...body
    }: {
      ticketId: number
      status?: string
      priority?: string
      assignee_id?: number
    }) => api.patch<AdminTicket>(`/admin/tickets/${ticketId}`, body),
    onSuccess: invalidate,
  })
}

export function useReplyToTicket(ticketId: number) {
  const invalidate = useInvalidateAdmin()
  return useMutation({
    mutationFn: (input: { body: string; resolve?: boolean }) =>
      api.post<TicketMessage>(`/admin/tickets/${ticketId}/messages`, input),
    onSuccess: invalidate,
  })
}

/* --- audit ----------------------------------------------------------------------- */

export function useAuditLog(filters: {
  action?: string
  user_id?: number
  entity?: string
  offset?: number
}) {
  const query = search({ ...filters, limit: PAGE_SIZE })
  return useQuery({
    queryKey: adminKeys.audit(query),
    queryFn: () => api.get<Page<AuditEntry>>(`/admin/audit?${query}`),
    placeholderData: (previous) => previous,
  })
}
